import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import moment from "moment";
import { useUpdateCompanyByIdMutation } from "../../../../../../../API/authApi";
import useLanguagesHooks from "../../../../../../../Components/Languages/setLanguage-hooks";
import useUploadFile from "../../../../../../../Components/FileUploader/fileUploader-hooks";
import { CustomerCount, IAutocompleteItem, ImageTypes } from "../../../../../../../Interfaces/componentTypes";
import { ICreateBUsinessProfileForm } from "../../../../../../../Interfaces/formValue-interfaces";
import { useToastify } from "../../../../../../../Hooks/hooks";
import { customersCount, weekDays } from "../../../../../../../Costants/costants";
import { IUpdateCompanyById } from "../../../../../../../Interfaces/request-interfaces";
import { IEditBusinessProfileMainProps } from "./profileMainInfo";

const useBusinessMainProfileHooks = (props: IEditBusinessProfileMainProps) => {
    const { data, onUpdate } = props;
    const { t } = useLanguagesHooks();
    const [isFullWeek, setIsFullWeek] = useState<boolean>(false);
    const [is24, setIs24] = useState<boolean>(false);
    const { imageUrl, handleFileInputChange, ImageResponce, imageLoading } = useUploadFile(ImageTypes.LOGO);
    const [update, { data: updatedData, isSuccess: isUpdated, isError: isUpdateError, isLoading }] = useUpdateCompanyByIdMutation();
    const {
        register,
        control,
        formState: { errors, isValid },
        setValue,
        getValues,
        setError,
        trigger,
        watch
    } = useForm<ICreateBUsinessProfileForm>({ mode: 'all' });
    const freeDays = watch("free_days");
    const startTime = watch('start_time');
    const endTime = watch('end_time');
    const [country, setCountry] = useState<string | null>(data.country);
    const { showToastError, showToastSuccess } = useToastify();
    const [isFormChanged, setIsFormChanged] = useState(false);
    const isWorkingDays = (Array.isArray(freeDays) && freeDays.length > 0) || isFullWeek;
    const isTimeSet = (startTime && endTime) || is24;
    const isProfile = !!data.business_profile;
    const validated = isValid && isWorkingDays && isTimeSet && isFormChanged;
    const scrollTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const createDateFromTimeString = (time: string): Date => {
        const [hour, minute] = time.split(':').map(Number);

        return moment().set({
            hour,
            minute,
            second: 0,
            millisecond: 0,
        }).toDate();
    };

    useEffect(() => {
        if (data) {
            setValue('name', data.name);
            setValue('country', data.country);
            setValue('city', data.city);
            setValue('customer_count', customersCount.find((item) => item.id === data.customer_count) as IAutocompleteItem);
            if (isProfile) {
                setValue('address', data.business_profile?.address);
                setValue('free_days', weekDays.filter((day) => data.business_profile?.free_days.includes(+day.id)));
                setValue('start_time',
                    data.business_profile?.start_time ? createDateFromTimeString(data.business_profile?.start_time) : null
                );
                setValue('end_time',
                    data.business_profile?.end_time ? createDateFromTimeString(data.business_profile?.end_time) : null
                );
                setIsFullWeek(!!data.business_profile?.full_week);
                setIs24(!!data.business_profile?.is_24)

            }
        }
    }, [data, setValue]);

    const getInitialValues = (): Partial<ICreateBUsinessProfileForm> => ({
        name: data.name,
        country: data.country,
        city: data.city,
        customer_count: customersCount.find((item) => item.id === data.customer_count),
        address: data.business_profile?.address,
        free_days: weekDays.filter((day) => data.business_profile?.free_days.includes(+day.id)),
        start_time: data.business_profile?.start_time
            ? createDateFromTimeString(data.business_profile?.start_time)
            : null,
        end_time: data.business_profile?.end_time
            ? createDateFromTimeString(data.business_profile?.end_time)
            : null,
    });

    useEffect(() => {
        if (imageUrl && ImageResponce) {
            setIsFormChanged(true);
            trigger()
        }
    }, [imageUrl, ImageResponce]);


    useEffect(() => {
        const subscription = watch((value) => {
            const currentValues = {
                ...value,
                customer_count: value.customer_count,
                start_time: value.start_time ?? null,
                end_time: value.end_time ?? null,
            };

            const initialValues = getInitialValues();

            const hasChanged = JSON.stringify(currentValues) !== JSON.stringify(initialValues)
                || is24 !== !!data.business_profile?.is_24
                || isFullWeek !== !!data.business_profile?.full_week
                || (!!ImageResponce && ImageResponce.src !== data.logo);

            setIsFormChanged(hasChanged);
        });

        return () => subscription.unsubscribe();
    }, [watch, data, is24, isFullWeek, ImageResponce]);


    useEffect(() => {
        if (isUpdated && updatedData) {
            onUpdate();
            showToastSuccess(t('Toast.Success.Common'));
            setIsFormChanged(false);
            scrollTop();
        } else if (isUpdateError) {
            showToastError(t('Toast.Error.Common'))
        }
    }, [isUpdated, updatedData, isUpdateError]);

    const hanedleFullWeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        setIsFullWeek(isChecked);
        isChecked && setValue('free_days', undefined);
        trigger();
    };

    const handle24 = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        setIs24(isChecked);
        trigger();
    };

    useEffect(() => {
        if (Array.isArray(freeDays) && freeDays.length > 0) {
            setIsFullWeek(false);
        }
    }, [freeDays]);

    useEffect(() => {
        if (startTime || endTime) {
            setIs24(false);
            if (!endTime) {
                setError('end_time', { message: "" })
            } else if (!startTime) {
                setError('start_time', { message: "" })
            }
        }
    }, [startTime, endTime]);

    useEffect(() => {
        if (is24) {
            setValue('start_time', null);
            setValue('end_time', null)
        }
    }, [is24]);

    const onEdit = () => {
        const freeDays = getValues('free_days')?.map((day) => +day.id);
        const startTime = getValues('start_time') ? String(moment(new Date(String(getValues('start_time')))).format('HH:mm')) : null;
        const endTime = getValues('end_time') ? String(moment(new Date(String(getValues('end_time')))).format('HH:mm')) : null;
        const payload: IUpdateCompanyById = {
            id: data.id,
            data: {
                name: getValues().name,
                country: getValues().country,
                city: getValues().city,
                
                customer_count: (getValues().customer_count as IAutocompleteItem).id as CustomerCount,
                logo: ImageResponce?.src ?? data.logo,
                business_profile: {
                    address: getValues('address'),
                    free_days: freeDays ?? [],
                    start_time: startTime,
                    end_time: endTime,
                    is_24: is24,
                    full_week: isFullWeek
                },
            }
        };
        update(payload)
    };

    return {
        is24,
        errors,
        control,
        country,
        imageUrl,
        isLoading,
        validated,
        isProfile,
        isFullWeek,
        imageLoading,
        isFormChanged,
        onEdit,
        setValue,
        register,
        handle24,
        setCountry,
        hanedleFullWeek,
        handleFileInputChange,
    };
};

export default useBusinessMainProfileHooks;
